import { defineNuxtPlugin, useRouter } from '#imports'
import type { LocationQuery, Router } from 'vue-router'

/**
 * First + Last-Touch Attribution Bridge (v1.16.0 — ports audiogroup-umami-events.php)
 *
 * v1.16.0 (2026-08-12): self-heal for touches stored before the hygiene existed —
 * `healPollutedTouch()` drops a stored first/last touch whose source is a blocked
 * host. Without it, a browser already holding `first_utm_source=www.paypal.com`
 * keeps attaching it to every purchase for up to 90 days (first-write-wins).
 *
 * v1.15.0 (2026-08-12): referrer hygiene, round two — the referrer is ignored
 * entirely on payment-return landing paths (NO_REFERRER_PATHS), and the
 * search/AI table lookup matches on label boundaries instead of substrings.
 * Both close gaps found reviewing v1.14.0 against the live Umami data.
 *
 * v1.14.0 (2026-08-11): referrer hygiene — payment/auth redirects blocked,
 * self-referral compared on the registrable domain, search engines mapped to
 * `medium=organic` with a normalised source, AI assistants to `medium=ai`.
 * See the BLOCK_HOSTS/SEARCH_HOSTS/AI_HOSTS block below for the rationale.
 *
 * Persists the marketing touch of a visit into localStorage so the eventual
 * `purchase` event (fired server-side from WP) can attribute back to the
 * campaign, surviving PayPal / Klarna / Stripe / 3-D-Secure redirect-hops.
 *
 * Resolves each visit's touch: UTM > paid click-id (gclid/fbclid) > external
 * referrer (organic/referral) > direct (skip). Then:
 *   - FIRST touch: first-write-wins, with a 90-day best-effort expiry
 *     (Safari/iOS ITP may evict script-written localStorage sooner).
 *   - LAST touch:  last-non-direct-wins (only visits carrying a real touch
 *     overwrite it; a direct/internal navigation must not).
 *
 * Storage keys (kept identical to the WP mu-plugin schema, both first_ and last_):
 *   {first|last}_utm_source|medium|campaign|content|term|timestamp|landing
 *
 * SPA nuance: document.referrer only reflects the external entry on the INITIAL
 * load — it does NOT change on client-side navigation. So the referrer fallback
 * is applied on initial load ONLY; SPA route changes consider UTM/gclid/fbclid
 * from the new query, never the (stale) referrer.
 *
 * .client.ts → browser only, never runs during SSR.
 */

const UTM_SHORT = ['source', 'medium', 'campaign', 'content', 'term'] as const
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000

/**
 * Referrer hygiene (v1.14.0) — kept byte-for-byte in step with the WP mu-plugin
 * `audiogroup-umami-events.php`. Divergence between the shops is the exact
 * problem this pass exists to remove, so change both or neither.
 *
 * Payment and auth redirects return the customer to the thank-you page carrying
 * their own referrer; unfiltered they overwrite the real touch. Measured
 * 2026-08-11 on BOOM: PayPal was the last-click source on 15 of 17 attributable
 * conversions and held `last_utm_source` on 5 purchases.
 */
const BLOCK_HOSTS = [
    'paypal.com', 'paypalobjects.com',
    'checkout.com', // incl. authentication-devices.checkout.com (3DS)
    'klarna.com', 'klarna.net',
    'braintreegateway.com',
    'stripe.com', 'adyen.com', 'mollie.com', 'sofort.com', 'giropay.de',
    'accounts.google.com', 'pay.google.com',
] as const

/** Search engines → medium 'organic' with a normalised source, so SEO is its own
 *  channel and does not fragment against the 'google' written by the gclid path. */
const SEARCH_HOSTS: ReadonlyArray<readonly [string, string]> = [
    ['google.', 'google'], ['bing.', 'bing'], ['duckduckgo.', 'duckduckgo'],
    ['brave.', 'brave'], ['yandex.', 'yandex'], ['ecosia.', 'ecosia'],
    ['yahoo.', 'yahoo'], ['baidu.', 'baidu'], ['qwant.', 'qwant'],
    ['startpage.', 'startpage'],
]

/** AI assistants get their own medium per UTM-CONVENTION.md. */
const AI_HOSTS: ReadonlyArray<readonly [string, string]> = [
    ['chatgpt.', 'chatgpt'], ['openai.', 'chatgpt'], ['perplexity.', 'perplexity'],
    ['copilot.', 'copilot'], ['claude.ai', 'claude'], ['gemini.google.', 'gemini'],
]

/**
 * Landing paths where an external referrer is NEVER a marketing touch: a payment
 * or auth provider is handing the customer back mid-purchase.
 *
 * This closes the class BLOCK_HOSTS can only enumerate. A 3-D-Secure challenge
 * runs on the card issuer's ACS host (arcot.com, cardinalcommerce.com, or the
 * bank's own domain) and bank redirect methods return via the bank — those hosts
 * are unbounded, so no block list can cover them. A real marketing click never
 * lands on the checkout or the order-received page first, so the path is the
 * reliable signal where the host is not.
 */
const NO_REFERRER_PATHS = ['/checkout', '/order-received', '/order-pay'] as const

function hostIs(host: string, suffix: string): boolean {
    return host === suffix || host.slice(-(suffix.length + 1)) === `.${suffix}`
}

function baseDomain(host: string): string {
    const parts = host.split('.')
    return parts.length > 2 ? parts.slice(-2).join('.') : host
}

/**
 * Table lookup on label boundaries, not substrings.
 *
 * Two entry shapes, deliberately handled differently:
 *   'google.'   — brand label + any TLD: matches google.de and news.google.com,
 *                 but not mygoogle.io (which a plain `includes` did match, and
 *                 then counted as organic Google traffic).
 *   'claude.ai' — full domain: matches claude.ai and www.claude.ai, but not
 *                 notclaude.ai and not claude.ai.example.com.
 *
 * Residual, accepted: 'google.evil.com' still matches the brand-label shape.
 * Closing that needs a public-suffix list; the only consequence here is a
 * referral counted as organic, so it is not worth the dependency.
 */
function lookupHost(host: string, table: ReadonlyArray<readonly [string, string]>): string | null {
    for (const [match, name] of table) {
        if (match.endsWith('.')) {
            if (host.startsWith(match) || host.includes(`.${match}`)) return name
        } else if (hostIs(host, match)) {
            return name
        }
    }
    return null
}

/**
 * Self-heal for touches stored BEFORE the referrer hygiene existed.
 *
 * Stopping new contamination is not enough: `first_utm_*` is first-write-wins with
 * a 90-day expiry, so a browser already holding `first_utm_source=www.paypal.com`
 * keeps sending it with every purchase until that expiry rolls over. Last-touch
 * recovers on its own only if the visitor returns with a real touch — a direct
 * visitor keeps the wrong value indefinitely.
 *
 * So: on load, drop a stored touch whose source is a blocked host.
 *
 * CRITICAL — only hostnames are contamination. Sonuscore has real purchases with
 * `orig_utm_source = 'klarna'`, which is a genuine `utm_source` campaign parameter,
 * not a referrer. `hostIs()` does not match a bare token against `klarna.com`, so
 * it survives. Never widen this to a substring match.
 *
 * Runs on every load and is idempotent — once cleared, nothing matches again, so
 * it needs no migration flag.
 */
function healPollutedTouch(prefix: 'first' | 'last') {
    const source = localStorage.getItem(`${prefix}_utm_source`)
    if (!source) return
    const host = source.toLowerCase()
    if (!BLOCK_HOSTS.some((h) => hostIs(host, h))) return
    // Drop the whole set — a lone cleared source would leave an inconsistent touch.
    for (const k of UTM_SHORT) localStorage.removeItem(`${prefix}_utm_${k}`)
    localStorage.removeItem(`${prefix}_utm_timestamp`)
    localStorage.removeItem(`${prefix}_utm_landing`)
}

type UtmQuery = LocationQuery | Record<string, string | string[] | null | undefined>
type Touch = Record<(typeof UTM_SHORT)[number], string>

function readParam(query: UtmQuery, key: string): string {
    const raw = query[key]
    // First-occurrence-wins for arrays — matches PHP $_GET semantics.
    return typeof raw === 'string'
        ? raw
        : Array.isArray(raw) && typeof raw[0] === 'string'
          ? raw[0]
          : ''
}

function resolveTouch(query: UtmQuery, useReferrer: boolean): Touch | null {
    const utm = {} as Touch
    let hasUtm = false
    for (const k of UTM_SHORT) {
        const v = readParam(query, `utm_${k}`)
        if (v) hasUtm = true
        utm[k] = v
    }
    if (hasUtm) return utm
    if (readParam(query, 'gclid')) return { source: 'google', medium: 'cpc', campaign: '', content: '', term: '' }
    if (readParam(query, 'fbclid')) return { source: 'facebook', medium: 'paid_social', campaign: '', content: '', term: '' }
    if (useReferrer && document.referrer) {
        try {
            const refHost = new URL(document.referrer).hostname.toLowerCase()
            if (!refHost) return null
            // Payment/auth return by landing path — see NO_REFERRER_PATHS.
            const path = window.location.pathname.toLowerCase()
            if (NO_REFERRER_PATHS.some((p) => path.startsWith(p))) return null
            // Self-referral by registrable domain. The old test (indexOf) let
            // 'asoundeffect.com' through while the host was 'www.asoundeffect.com'.
            const isSelf = baseDomain(refHost) === baseDomain(window.location.hostname.toLowerCase())
            const isBlocked = BLOCK_HOSTS.some((h) => hostIs(refHost, h))
            // A blocked payment/auth return yields no touch at all — that is the
            // fix: last-touch keeps the source the customer actually arrived from.
            if (isSelf || isBlocked) return null

            const ai = lookupHost(refHost, AI_HOSTS) // before search: gemini.google.* is AI, not Google
            if (ai) return { source: ai, medium: 'ai', campaign: '', content: '', term: '' }
            const se = lookupHost(refHost, SEARCH_HOSTS)
            if (se) return { source: se, medium: 'organic', campaign: '', content: '', term: '' }
            return { source: refHost.replace(/^www\./, ''), medium: 'referral', campaign: '', content: '', term: '' }
        } catch {
            // malformed referrer — ignore
        }
    }
    return null
}

function writeSet(prefix: 'first' | 'last', touch: Touch, now: string) {
    for (const k of UTM_SHORT) localStorage.setItem(`${prefix}_utm_${k}`, touch[k] || '')
    localStorage.setItem(`${prefix}_utm_timestamp`, now)
    localStorage.setItem(`${prefix}_utm_landing`, window.location.pathname)
}

function persistTouch(query: UtmQuery, useReferrer: boolean) {
    let touch: Touch | null
    try {
        touch = resolveTouch(query, useReferrer)
    } catch {
        return
    }
    if (!touch) return // direct / internal navigation — no signal, do not overwrite

    try {
        const now = new Date().toISOString()
        // FIRST — first-write-wins + 90-day best-effort expiry.
        const firstTs = localStorage.getItem('first_utm_timestamp')
        const expired = !!firstTs && Date.now() - new Date(firstTs).getTime() > MAX_AGE_MS
        if (!localStorage.getItem('first_utm_source') || expired) writeSet('first', touch, now)
        // LAST — last-non-direct-wins.
        writeSet('last', touch, now)
    } catch {
        // localStorage disabled or full — degrade gracefully (attribution just weaker).
    }
}

// Marker to keep router.afterEach registration idempotent across HMR reloads.
type RouterWithMarker = Router & { __utmBridgeAttached?: true }

export default defineNuxtPlugin(() => {
    // 0) Drop pre-v1.14.0 contamination before anything reads it, so a fresh touch
    //    on this very visit can take the slot that a payment host was holding.
    try {
        healPollutedTouch('first')
        healPollutedTouch('last')
    } catch {
        // localStorage disabled — nothing to heal, nothing to break
    }

    // 1) Initial load — capture from window.location.search (router.afterEach does
    //    not fire for the initial SSR/hydration route). Referrer fallback applies here.
    const initialQuery = Object.fromEntries(new URLSearchParams(window.location.search))
    persistTouch(initialQuery, true)

    // 2) SPA navigations — UTM/gclid/fbclid only (no referrer: it is stale on SPA nav).
    const router = useRouter() as RouterWithMarker
    if (router.__utmBridgeAttached) return
    router.__utmBridgeAttached = true
    router.afterEach((to) => {
        persistTouch(to.query, false)
    })
})
